import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Box } from "@mui/material";
import useAuth from "../../context/AuthContext";
import { CreatePageList } from "@app/components/Layouts/PageList";
import { Loading, MButton } from "@app/components";
import MessageDialog from "@app/components/Login/MessageDialog";
import { errorAlert } from "@app/utils/alerts";
import { IFormTransitDose } from "@app/types/transitDose.types";
import { useTransitDose } from "./utils/useTransitDose";
import { getDefaultValuesTransitDose, mappingDoses } from "./utils/helpers";
import TransitDosesForm from "./components/transitDosesForm";

const EditTransit = () => {
  const { idTransitDoses } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [dataToSave, setDataToSave] = useState<IFormTransitDose | null>(null);
  const { get, loadingSave, save } = useTransitDose();

  const form = useForm<IFormTransitDose>({
    mode: "all",
    defaultValues: {},
  });

  const { reset } = form;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (idTransitDoses) {
          const transitDoses = get(Number(idTransitDoses));
          if (!transitDoses) {
            errorAlert("Dosis de tránsito no encontrada");
            navigate("/admin/transito");
            return;
          }
          const defaultValues = await getDefaultValuesTransitDose(
            transitDoses,
            user
          );
          reset(defaultValues);
        } else {
          const defaultValues = await getDefaultValuesTransitDose(
            undefined,
            user
          );
          reset(defaultValues);
        }
      } catch (error) {
        errorAlert("Error al cargar los valores predeterminados");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [idTransitDoses, reset, navigate, user]);

  const submit = async (data: IFormTransitDose) => {
    const status = await save(mappingDoses(data));
    if (!status) {
      navigate(-1);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleSave = (data: IFormTransitDose) => {
    if (idTransitDoses) {
      setDataToSave(data);
      setOpenConfirmModal(true);
    } else {
      submit(data);
    }
  };

  const handleDialogClose = () => setOpenConfirmModal(false);

  const handleDialogConfirm = () => {
    if (dataToSave) {
      submit(dataToSave);
    }
    handleDialogClose();
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSave)}>
          <CreatePageList
            title={
              idTransitDoses
                ? `Editar dosis de tránsito - ${idTransitDoses}`
                : "Nueva dosis de tránsito"
            }
            footer={
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                }}
              >
                <MButton
                  variant="contained"
                  color="primary"
                  text="Guardar"
                  type="submit"
                />

                <MButton
                  variant="outlined"
                  color="secondary"
                  sx={{ ml: 2 }}
                  text="Cancelar"
                  onClick={handleBack}
                />
              </Box>
            }
          >
            <TransitDosesForm />
          </CreatePageList>
        </form>
      </FormProvider>
      {loadingSave && <Loading />}
      <MessageDialog
        data={{
          component: "Confirm",
          title: "OJO: Va a proceder a Editar esta dosis de tránsito",
          message: "¿Desea continuar?",
          status: "confirm",
          active: openConfirmModal,
          handleConfirm: handleDialogConfirm,
        }}
        handleClose={handleDialogClose}
        handleConfirm={handleDialogConfirm}
      ></MessageDialog>
    </>
  );
};

export default EditTransit;
